import React, { useEffect, useState } from "react";
import { CheckCircle, CloudUpload, ShieldCheck } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import EmpolyeeUploadStep from "./EmpolyeeUploadStep";
import { EmpolyeeCheckValidation } from "../config/empolyeeApi"; // Make sure this is imported
import EmpolyeeCheckingStep from "./EmpolyeeCheckingStep";
import EmpolyeeResultStep from "./EmpolyeeResultStep";
import { Button, Col, Form, Row } from "react-bootstrap";
import FalconComponentCard from "components/common/FalconComponentCard";
import HorizontalStepper from "features/practitioner/components/HorizontalStepper";
import { AnimatePresence, motion } from "framer-motion";
import { setTableData } from "../config/empolyeeSlice";
import { ImportEmpolyee } from "../config/empolyeeApi";
import moment from "moment";
import WizardModal from "components/wizard/WizardModal";
import UploadBranchModal from "features/branch/components/UploadBranchModal";
import UploadEmpolyeeModal from "./UploadEmpolyeeModal";
import BackButton from "components/common/custom/back-button";
import paths from "routes/paths";

const EmpolyeeImport = () => {
  const dispatch = useDispatch();
  const { allPermissionType } = useSelector((state) => state.role);
  const empolyeeSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Employee"
  );
  const {
    excelData,
    validationErrors,
    tableData,
    isExcelValid,
    systemPrivillageList,
  } = useSelector((state) => state.empolyee);
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      excelFile: null,
    },
  });
  const [activeStep, setActiveStep] = useState(0);
  const [showFileError, setShowFileError] = useState(false);
  const [payload, setPayload] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showUploadEmpolyee, setShowUploadEmpolyee] = useState(false);

  const steps = [
    {
      label: "Upload",
      icon: CloudUpload,
      component: (
        <EmpolyeeUploadStep
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
        />
      ),
    },
    {
      label: "Checking",
      icon: ShieldCheck,
      component: <EmpolyeeCheckingStep />,
    },
    {
      label: "Result",
      icon: CheckCircle,
      component: <EmpolyeeResultStep />,
    },
  ];

  useEffect(() => {
    const data = excelData?.map((row, index) => {
      const rowNum = index + 2;
      const rowErrors = validationErrors?.filter((err) => err?.row === rowNum);

      return {
        ...row,
        rowNum,
        isValid: rowErrors?.length === 0,
        errors: rowErrors?.map((e) => e?.message),
      };
    });
    dispatch(setTableData(data));
  }, [excelData, validationErrors, dispatch]);

  const validateFile = async () => {
    const isValid = await trigger("excelFile");
    setShowFileError(!isValid);
    return isValid;
  };

  // Inside the component
  const handleContinue = async () => {
    if (activeStep === 0) {
      const isValid = await validateFile();
      if (isValid) {
        setActiveStep(1);
        setShowFileError(false);
      }
    } else if (activeStep === 1) {
      const data_check = {};

      tableData.forEach((row, index) => {
        const key = (index + 1).toString();

        data_check[key] = {
          employees_emailid: row["Email"] || row?.email || "",
          branch_cchi: Array.isArray(row["Branch"])
            ? row?.Branch?.map(
              (item) => item?.cchi || item?.branch_cchino
            )?.join(",")
            : row["Branch"] || "",
          employees_nationalid:
            row["National Id"]?.toString() || row?.national_id || "",
        };
      });

      const payload = { data_check };

      try {
        const response = await dispatch(
          EmpolyeeCheckValidation(payload)
        ).unwrap();

        const responseErrors = response?.data?.errors || [];

        if (responseErrors.length > 0) {
          // Map errors to rows by national id
          setShowDuplicateModal(true);
          const updatedTable = tableData.map((row) => {
            const nationalId =
              row["National Id"]?.toString() || row?.national_id || "";

            const matchingErrors = responseErrors.filter(
              (err) => err.nationalid === nationalId
            );

            if (matchingErrors.length > 0) {
              return {
                ...row,
                isValid: false,
                errors: [
                  ...(row.errors || []),
                  ...matchingErrors.map((e) => e.error_msg_en),
                ],
              };
            }
            return row;
          });

          dispatch(setTableData(updatedTable));

          // Stop progression
          return;
        }

        // Proceed if no errors
        setActiveStep(2);
      } catch (error) {
        console.error("Validation error:", error);
      }
    } else if (activeStep === steps?.length - 1) {
      const payload = {};

      tableData.forEach((row, index) => {
        const key = (index + 1).toString();

        payload[key] = {
          employees_firstname:
            row["First Name (English)"] || row?.first_name_eng || "",
          employees_firstname_arabic:
            row["First Name (Arabic)"] || row?.first_name_arabic || "",
          employees_fathername: "",
          employees_fathername_arabic: "",
          employees_familyname:
            row["Family Name (English)"] || row?.last_name_eng || "",
          employees_familyname_arabic:
            row["Family Name (Arabic)"] || row?.last_name_arabic || "",
          employees_mobilenumber: row["Mobile No"] || row?.mobile_no || "",
          employees_emailid: row["Email"] || row?.email || "",
          employees_dob:
            row["DOB"] || row?.birth_date
              ? moment(row["DOB"] || row?.birth_date).format("YYYY-MM-DD")
              : "" || "",
          employees_nationality:
            row["Nationality"] || row?.nationality?.value || "",
          branch_cchi: Array.isArray(row["Branch"])
            ? row["Branch"]
              .map((item) => item?.cchi || item?.branch_cchino)
              .join(",")
            : row["Branch"] || "",
          employees_nationalid:
            row["National Id"]?.toString() || row?.national_id || "",
          syspri_id:
            systemPrivillageList?.find(
              (item) => item?.syspri_name === row?.["System Privilage"]
            )?.syspri_id ||
            row?.system_privilage?.syspri_id ||
            "",
          employees_esspassword: row["Password"] || row?.password || "",
        };
      });

      const finalPayload = { emp_data: payload };
      setPayload(finalPayload);
      setShowUploadEmpolyee(true);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <>
      {Number(empolyeeSetting?.add) === 1 ? (
        <Form noValidate className="p-0">
          <FalconComponentCard
            className="shadow-md"
            style={{ position: "relative" }}
          >
            <FalconComponentCard.Header
              className="d-flex fixed-header justify-content-between align-items-center"
              sx={{
                position: "sticky !important",
                top: 68,
                zIndex: 1020,
                backgroundColor: "white",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <BackButton path={paths.employeeList} title="User Import" />

              <Col xs="auto" className="d-flex gap-2">
                {activeStep > 0 && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="success"
                  onClick={handleContinue}
                  disabled={
                    (activeStep === 0 && !isExcelValid) ||
                    (activeStep === 1 && tableData.some((row) => !row.isValid)) ||
                    tableData?.length === 0
                  }
                >
                  {activeStep === steps.length - 1 ? "Finish" : "Continue"}
                </Button>
              </Col>
            </FalconComponentCard.Header>
            <FalconComponentCard.Body>
              <Row>
                <Col xs={12}>
                  <HorizontalStepper
                    isHorizaontal
                    steps={steps}
                    activeStep={activeStep}
                  />
                </Col>
                <Col xs={12}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, x: -100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                    >
                      {steps[activeStep]?.component || null}
                    </motion.div>
                  </AnimatePresence>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Form>
      ) : (
        <div className="py-6 text-center border rounded-3mb-3">
          <h5 className="text-danger">Permission Denied</h5>
          <p className="text-muted fs-11">
            You do not have permission to import Users.
          </p>
        </div>
      )}
      <WizardModal
        modal={showDuplicateModal}
        setModal={() => setShowDuplicateModal(false)}
        title="Duplicate Users Record(s)"
        description="Each User must have a unique National ID and email and validation branch. please edit national id or email no or delete the record"
      />

      <UploadEmpolyeeModal
        payload={payload}
        onClose={() => setShowUploadEmpolyee(false)}
        show={showUploadEmpolyee}
      />
    </>
  );
};

export default EmpolyeeImport;
